import { useIntl } from 'react-intl'
import Hr from 'components/Common/Hr'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidBuildingIcon from 'LuxKit/Icons/solid/SolidBuildingIcon'
import { SEVEN_DAYS_AFTER_PURCHASE, FOURTEEN_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import FlexibleCancellationCharterFlightFees from './FlexibleCancellationCharterFlightFees'
import { TimelineNode, TimelineEndNode } from './CancellationPolicyTimelineComponents'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

interface Props {
  /** Days before check-in/departure for credit-only and non-refundable thresholds (e.g. 21 or 45). */
  days: number
  /** Whether this is a charter bundle (flights + hotel). Affects terminology and shows charter-specific fees. */
  isCharter?: boolean
}

function FlexibleCancellationPolicyTimelineModalWithoutDates(props: Props) {
  const { days, isCharter } = props
  const intl = useIntl()
  const fullRefundColour = useRebrandSuccessDarkColour()

  const checkInOrDepartureLabel = isCharter
    ? intl.formatMessage({
        id: 'dAYHFZkXhpQA6WMGvbmK',
        defaultMessage: 'Departure',
        description: 'Label for departure date on cancellation timeline without dates (charter)',
      })
    : intl.formatMessage({
        id: 'poCu5gwZHqlbN808pfIt',
        defaultMessage: 'Check-in',
        description: 'Label for check-in date on cancellation timeline without dates (non-charter)',
      })

  const noRefundLabel = isCharter
    ? intl.formatMessage({
        id: 'A0vlpq05IK1xYwLmMHpH',
        defaultMessage: 'From {days} days until departure',
        description: 'Timeline label for the no-refund period before departure in cancellation modal without dates (charter)',
      }, { days })
    : intl.formatMessage({
        id: 'tl7_BtwbsRRz0pGZvRke',
        defaultMessage: 'Fewer than {days} days before check-in',
        description: 'Timeline label for the no-refund period before check-in in cancellation modal without dates (non-charter)',
      }, { days })

  return (
    <>
      <Group direction="vertical">
        <TimelineNode
          label={intl.formatMessage({
            id: 'vjetCLF3dFVHyJnSwn31',
            defaultMessage: 'Within {days} days of purchase',
            description: 'Timeline label for the full refund period after purchase in cancellation modal without dates',
          }, { days: SEVEN_DAYS_AFTER_PURCHASE })}
        >
          <BodyText variant="medium" weight="bold" colour={fullRefundColour}>
            {intl.formatMessage({
              id: 'IPvnv3wz6i5O1pItlNwB',
              defaultMessage: 'Full refund',
              description: 'Section heading for full refund in no-dates cancellation modal',
            })}
          </BodyText>
          <Group direction="vertical" gap={8}>
            <BodyText variant="medium" as="p">
              {intl.formatMessage({
                id: 'VyE5pEAHa1hOdfIjp2TD',
                defaultMessage: 'Cancel within {days} days of purchase to receive a full refund to your original payment method or credit.',
                description: 'Full refund body in no-dates cancellation modal in a modal dialog',
              }, { days: SEVEN_DAYS_AFTER_PURCHASE })}
            </BodyText>
            <div>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'd68PXUpxHYTTkOyV6HWI',
                  defaultMessage: 'Conditions apply:',
                  description: 'Subsection heading for the conditions that apply to a promotional offer',
                })}
              </BodyText>
              <BodyText variant="medium">
                {isCharter && intl.formatMessage({
                  id: '5p9fFIBIsdjfCPNoamtK',
                  defaultMessage: 'The cancellation must be made at least {days} days before departure.',
                  description: 'Full refund condition text for charter in the cancellation or refund flow',
                }, { days: FOURTEEN_DAYS_BEFORE_CHECK_IN })}
                {!isCharter && intl.formatMessage({
                  id: 'aD3y8uk6ZrNvsIQInDof',
                  defaultMessage: 'The cancellation must be made at least {days} days before check-in.',
                  description: 'Full refund condition text for non-charter in the cancellation flow',
                }, { days: FOURTEEN_DAYS_BEFORE_CHECK_IN })}
              </BodyText>
            </div>
          </Group>
        </TimelineNode>

        {!isCharter && (
          <TimelineNode
            label={intl.formatMessage({
              id: '9e42YUlMptEMf3Fo2_tE',
              defaultMessage: 'More than {days} days before check-in',
              description: 'Timeline label for the credit refund period before check-in in cancellation modal without dates',
            }, { days })}
          >
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'BkK38vGr7Mr942F0vUUA',
                defaultMessage: 'Full refund to credit',
                description: 'Section heading for full credit refund in cancellation timeline modal',
              })}
            </BodyText>
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'ryBM_QsLa9xln8IqIZUw',
                defaultMessage: 'Cancel more than {days} days before check-in to receive a 100% refund to credit.',
                description: 'Credit-only refund for non-charter in the cancellation or refund flow',
              }, { days })}
            </BodyText>
          </TimelineNode>
        )}

        {isCharter && (
          <TimelineNode
            label={intl.formatMessage({
              id: 'k4zEXAAK16mZ04r4oxe9',
              defaultMessage: 'Up to {days} days prior to departure',
              description: 'Timeline label for the partial refund period before departure in cancellation modal without dates (charter)',
            }, { days: days + 1 })}
          >
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'KsmgOLy4pOps0C4ljO4T',
                defaultMessage: 'Partial refund',
                description: 'Section heading for partial/credit refund in a modal dialog',
              })}
            </BodyText>
            <Group direction="vertical" gap={8}>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'Ke5udm-SsFdx4U4Ew4yz',
                  defaultMessage: 'If you cancel at least {days} days before departure, a cancellation fee will apply:',
                  description: 'Partial refund intro for charter in no-dates modal',
                }, { days })}
              </BodyText>
              <FlexibleCancellationCharterFlightFees />
            </Group>
          </TimelineNode>
        )}

        <TimelineNode label={noRefundLabel}>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'yAazKuRHWgNT7wkPi4c9',
              defaultMessage: 'No refund',
              description: 'Section heading for no-refund period in a modal dialog',
            })}
          </BodyText>
          <Group direction="vertical" gap={8}>
            <BodyText variant="medium">
              {isCharter && intl.formatMessage({
                id: '2Uf9qchxueheLUX4-6KY',
                defaultMessage: 'Cancellations made within {days} days of departure are non-refundable.',
                description: 'Cancellation policy text explaining non-refundable period for charter flight bookings',
              }, { days })}
              {!isCharter && intl.formatMessage({
                id: '1Y2aN6fen5SPgReqFeDM',
                defaultMessage: 'Cancellations made fewer than {days} days before check-in are non-refundable.',
                description: 'No refund body for non-charter in the cancellation or refund flow',
              }, { days })}
            </BodyText>
            <BodyText variant="medium">
              {isCharter && intl.formatMessage({
                id: 'lO9Ctg1dfdT5jcXCWV_m',
                defaultMessage: 'However, you can still receive a full refund if you cancel within {purchaseDays} days of purchase and at least {beforeDepartureDays} days before departure.',
                description: 'Exception for full refund within 7 days and 14 before departure',
              }, {
                purchaseDays: SEVEN_DAYS_AFTER_PURCHASE,
                beforeDepartureDays: FOURTEEN_DAYS_BEFORE_CHECK_IN,
              })}
              {!isCharter && intl.formatMessage({
                id: 'w4J4qQ1yQ2Jobi2jVTBY',
                defaultMessage: 'You can still receive a full refund if you cancel within {purchaseDays} days of purchase and at least {beforeDepartureDays} days before check-in.',
                description: 'Exception for full refund within 7 days and 14 before check-in',
              }, {
                purchaseDays: SEVEN_DAYS_AFTER_PURCHASE,
                beforeDepartureDays: FOURTEEN_DAYS_BEFORE_CHECK_IN,
              })}
            </BodyText>
          </Group>
        </TimelineNode>

        <TimelineEndNode label={checkInOrDepartureLabel} icon={!isCharter ? SolidBuildingIcon : undefined} />
      </Group>

      <Hr size="md" />

      <div>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'zOgZxgWqO_0Kba00Iaqk',
            defaultMessage: 'Date changes',
            description: 'Section heading for date changes in the date selection or calendar picker',
          })}
        </BodyText>
        <BodyText variant="medium">
          {isCharter && intl.formatMessage({
            id: 'zfB933FaHQfL74MWwc7L',
            defaultMessage: 'One change permitted if made more than {days} days before departure. Subject to availability and applicable surcharges (peak periods or property-specific).',
            description: 'Date change policy text for charter in the date selection or calendar picker',
          }, { days })}
          {!isCharter && intl.formatMessage({
            id: '4NeNYPfYSbTZy4mxmC8m',
            defaultMessage: 'One change permitted if made more than {days} days before check-in. Subject to availability and applicable surcharges (peak periods or property-specific).',
            description: 'Date change policy text for non-charter in the date selection or calendar picker',
          }, { days })}
        </BodyText>
      </div>
    </>
  )
}

export default FlexibleCancellationPolicyTimelineModalWithoutDates
